import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Check,
  House,
  Hammer,
  Paintbrush,
  ShieldCheck,
  HardHat,
  Star,
} from "lucide-react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_SRC = "/building-trends-logo.jpeg";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85",
  builder: "https://images.unsplash.com/photo-1614127938540-a1139bee1841?crop=entropy&cs=srgb&fm=jpg&q=85",
  p1: "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85",
  p2: "https://images.unsplash.com/photo-1698994705178-d244d73ea573?crop=entropy&cs=srgb&fm=jpg&q=85",
  p3: "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&q=85",
  p4: "https://images.unsplash.com/photo-1602193230480-7840a38eb0c3?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const SERVICES = [
  {
    num: "01",
    icon: House,
    title: "Roofing",
    desc: "Premium shingles, metal & flat roofing built to weather every storm.",
    bullets: ["Storm Damage Repair", "Full Roof Replacement", "25-Yr Workmanship"],
  },
  {
    num: "02",
    icon: Hammer,
    title: "Construction",
    desc: "Ground-up custom builds with structural integrity and modern design.",
    bullets: ["Custom Homes", "Framing & Foundations", "Permit Handling"],
  },
  {
    num: "03",
    icon: Paintbrush,
    title: "Renovations",
    desc: "Interior remodels that transform spaces without losing soul.",
    bullets: ["Kitchens & Baths", "Open-Concept Conversions", "Finish Carpentry"],
  },
  {
    num: "04",
    icon: ShieldCheck,
    title: "Exteriors",
    desc: "Siding, gutters, and weatherproofing that protect what you've built.",
    bullets: ["Fiber-Cement Siding", "Seamless Gutters", "Waterproof Sealing"],
  },
];

const WHY_ITEMS = [
  { num: "01", title: "Licensed Master Builders", desc: "Every crew lead carries 10+ years and full state licensing." },
  { num: "02", title: "Fixed Quotes, No Surprises", desc: "Transparent estimates locked before a single nail goes in." },
  { num: "03", title: "25-Year Workmanship Promise", desc: "We stand behind our work decades after the keys change hands." },
  { num: "04", title: "Locally Owned & Insured", desc: "Fully insured, owner-operated, and rooted in your community." },
];

const TESTIMONIALS = [
  { quote: "Building Trends replaced our roof during the worst storm season and finished a week early. Clean site every evening, real updates, zero drama.", name: "Marcus Hale", role: "Homeowner · Westhill" },
  { quote: "They built our dream home from foundation to finish. The level of craft is honestly hard to find anymore — every detail considered.", name: "Priya Kapoor", role: "Custom Home Client" },
  { quote: "Renovated our kitchen and primary bath. Quote held exactly. Crew was respectful, fast, and the finish work is gallery-grade.", name: "Daniel Reyes", role: "Renovation Client" },
];

const MARQUEE_ITEMS = ["ROOFING","FRAMING","RENOVATIONS","EXTERIORS","SIDING","GUTTERS","WATERPROOFING","INSULATION","DESIGN-BUILD"];

// ---------- Reveal hook (intersection observer applied via class) ----------
function useRevealAll() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ---------- Logo block ----------
function LogoBlock({ small = false }) {
  return (
    <a href="#home" className="flex items-center gap-3" data-testid="nav-logo">
      <img
        src={LOGO_SRC}
        alt="Building Trends"
        className={small ? "h-9 w-9 object-cover" : "h-11 w-11 object-cover"}
      />
      <div className="leading-tight">
        <div className="font-display text-lg md:text-xl text-white tracking-[0.04em]">
          BUILDING <span className="text-[#5FD4D6]">TRENDS</span>
        </div>
        <div className="text-[10px] tracking-[0.32em] text-[#8A95A5] mt-0.5 font-medium">
          ROOFING · CONSTRUCTION
        </div>
      </div>
    </a>
  );
}

// ---------- Nav ----------
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#services", label: "Services" },
    { href: "#projects", label: "Projects" },
    { href: "#why", label: "Why Us" },
    { href: "#reviews", label: "Reviews" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-blur border-b border-[#2A3F54]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <LogoBlock />
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              href={l.href}
              className="text-xs tracking-[0.24em] uppercase font-semibold text-white/85 hover:text-[#5FD4D6] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <a
          data-testid="nav-quote-btn"
          href="#contact"
          className="bt-btn-primary hidden lg:inline-flex"
          style={{ padding: "14px 22px" }}
        >
          Get a Quote <ArrowRight size={16} />
        </a>
        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-[#0F1722] border-t border-[#2A3F54]">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-3 text-white text-sm font-semibold tracking-[0.2em] uppercase border-b border-[#2A3F54]"
              >
                {l.label}
              </a>
            ))}
            <a
              data-testid="mobile-nav-quote-btn"
              href="#contact"
              onClick={() => setOpen(false)}
              className="bt-btn-primary mt-4 justify-center"
            >
              Get a Quote <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

// ---------- Hero ----------
function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-28 md:pt-32 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img alt="Modern house construction" className="w-full h-full object-cover" src={IMAGES.hero} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1722] via-[#0F1722]/85 to-[#0F1722]/40" />
        <div className="absolute inset-0 bt-grid-texture opacity-60" />
        <div className="absolute inset-0 bt-vignette" />
      </div>
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center w-full py-20">
        <div className="lg:col-span-7 bt-rise">
          <div className="bt-eyebrow mb-6" data-testid="hero-eyebrow">
            Established Trust · Premium Builds
          </div>
          <h1
            data-testid="hero-heading"
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] text-white"
          >
            We Build
            <br />
            What Stands<span className="text-[#5FD4D6]">.</span>
          </h1>
          <p className="mt-7 max-w-xl text-[#B6C0CC] text-lg leading-relaxed">
            Building Trends is a full-service roofing and construction studio crafting durable,
            design-forward homes. From rooftops to renovations, every nail is placed with intention.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a data-testid="hero-cta-estimate" href="#contact" className="bt-btn-primary">
              Request Free Estimate <ArrowRight size={16} />
            </a>
            <a data-testid="hero-cta-projects" href="#projects" className="bt-btn-ghost">
              View Projects <ArrowUpRight size={16} />
            </a>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { num: "12+", label: "Years Building" },
              { num: "480+", label: "Homes Delivered" },
              { num: "100%", label: "Licensed & Insured" },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="border-l border-[#2A3F54] pl-4"
              >
                <div className="font-display text-3xl md:text-4xl text-white">{s.num}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8A95A5] mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden lg:block lg:col-span-5">
          <div className="relative aspect-[4/5] bt-grain border border-[#2A3F54] overflow-hidden zoom-wrap">
            <img alt="Professional builder" className="zoom-img" src={IMAGES.builder} />
            <div className="absolute bottom-0 inset-x-0 p-6 bg-gradient-to-t from-[#0F1722] to-transparent">
              <div className="bt-eyebrow !text-white mb-2" style={{ color: "#fff" }}>
                Crew On Site
              </div>
              <div className="font-display text-2xl text-white">Certified Master Builders</div>
            </div>
            <div className="absolute top-5 right-5 bg-[#5FD4D6] text-[#0F1722] px-3 py-1.5 text-xs font-display tracking-[0.18em]">
              Live Project
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Marquee ----------
function MarqueeTicker() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div data-testid="services-ticker" className="border-y border-[#2A3F54] bg-[#080C13] overflow-hidden">
      <div className="flex bt-marquee whitespace-nowrap py-5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-6 px-8">
            <span className="font-display tracking-[0.28em] text-2xl text-white/60">{it}</span>
            <span className="text-[#5FD4D6] text-xl">◆</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Services ----------
function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16 reveal">
          <div className="lg:col-span-7">
            <div className="bt-eyebrow mb-5">What We Do</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1] text-white">
              Built To Last.
              <br />
              <span className="text-[#5FD4D6]">Designed To Stand Out.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#B6C0CC] text-lg">
            Four core disciplines, one promise: meticulous craftsmanship at every level. Every
            project is led by a master builder and signed off by our quality desk.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 border border-[#2A3F54] reveal">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const showRight = i < SERVICES.length - 1;
            const showBottomMd = i < 2;
            return (
              <div
                key={s.num}
                data-testid={`service-card-${s.title.toLowerCase()}`}
                className={`group p-8 lg:p-10 bg-[#1B2B3A]/40 transition-all duration-300 hover:bg-[#1B2B3A] ${
                  showRight ? "lg:border-r border-[#2A3F54]" : ""
                } ${showBottomMd ? "border-b lg:border-b-0 border-[#2A3F54]" : ""}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="h-14 w-14 border border-[#2A3F54] flex items-center justify-center text-[#5FD4D6] group-hover:bg-[#5FD4D6] group-hover:text-[#0F1722] transition-colors">
                    <Icon size={26} strokeWidth={1.5} />
                  </div>
                  <span className="font-display text-[#2A3F54] text-3xl">{s.num}</span>
                </div>
                <h3 className="font-display text-2xl text-white mb-3 tracking-tight">{s.title}</h3>
                <p className="text-[#8A95A5] text-sm leading-relaxed mb-6">{s.desc}</p>
                <ul className="space-y-2">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/80">
                      <Check size={14} className="text-[#5FD4D6]" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- Projects ----------
function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-28 md:py-36 bg-[#080C13]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 reveal">
          <div>
            <div className="bt-eyebrow mb-5">Recent Work</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1] text-white">
              Projects That
              <br />
              <span className="text-[#5FD4D6]">Speak For Themselves.</span>
            </h2>
          </div>
          <a
            data-testid="projects-cta"
            href="#contact"
            className="bt-btn-ghost self-start lg:self-end"
          >
            Start Your Project <ArrowUpRight size={16} />
          </a>
        </div>

        <div className="grid grid-cols-12 gap-6 reveal">
          {/* Large left */}
          <div
            data-testid="project-card-0"
            className="col-span-12 lg:col-span-8 group relative overflow-hidden border border-[#2A3F54] aspect-[16/10] zoom-wrap"
          >
            <img alt="Westhill Residence" className="zoom-img" src={IMAGES.p1} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080C13] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="text-xs uppercase tracking-[0.28em] text-[#5FD4D6] mb-1">
                Full Roof Replacement
              </div>
              <div className="font-display text-3xl text-white">Westhill Residence</div>
            </div>
          </div>
          {/* Right stack */}
          <div className="col-span-12 lg:col-span-4 grid grid-cols-1 gap-6">
            <div
              data-testid="project-card-1"
              className="group relative overflow-hidden border border-[#2A3F54] aspect-[16/10] lg:aspect-auto lg:h-full zoom-wrap"
            >
              <img alt="Lakeside Custom Build" className="zoom-img" src={IMAGES.p2} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080C13] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#5FD4D6]">
                  Ground-Up Construction
                </div>
                <div className="font-display text-xl text-white">Lakeside Custom Build</div>
              </div>
            </div>
            <div
              data-testid="project-card-2"
              className="group relative overflow-hidden border border-[#2A3F54] aspect-[16/10] lg:aspect-auto lg:h-full zoom-wrap"
            >
              <img alt="Maple Street Remodel" className="zoom-img" src={IMAGES.p3} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080C13] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#5FD4D6]">
                  Interior Renovation
                </div>
                <div className="font-display text-xl text-white">Maple Street Remodel</div>
              </div>
            </div>
          </div>
          {/* Wide */}
          <div
            data-testid="project-card-3"
            className="col-span-12 group relative overflow-hidden border border-[#2A3F54] aspect-[21/7] zoom-wrap"
          >
            <img alt="Pinegate Exterior" className="zoom-img" src={IMAGES.p4} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#080C13] via-[#080C13]/30 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="text-xs uppercase tracking-[0.28em] text-[#5FD4D6] mb-1">
                Siding & Gutters
              </div>
              <div className="font-display text-3xl text-white">Pinegate Exterior</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Why Us ----------
function Why() {
  return (
    <section id="why" data-testid="why-section" className="relative py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative reveal">
          <div className="relative aspect-[4/5] border border-[#2A3F54] overflow-hidden bt-grain zoom-wrap">
            <img alt="Builder on roof" className="zoom-img" src={IMAGES.builder} />
          </div>
          <div className="absolute -bottom-6 -right-6 md:-right-10 bg-[#5FD4D6] p-6 md:p-8 max-w-[240px]">
            <HardHat size={28} className="text-[#0F1722] mb-3" />
            <div className="font-display text-2xl text-[#0F1722] leading-tight">
              Built By Pros.
              <br />
              Backed By Promise.
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <div className="bt-eyebrow mb-5">Why Building Trends</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1] text-white mb-10">
            The Standard, Not
            <br />
            <span className="text-[#5FD4D6]">The Exception.</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-px bg-[#2A3F54] border border-[#2A3F54]">
            {WHY_ITEMS.map((w) => (
              <div
                key={w.num}
                data-testid={`why-item-${w.num}`}
                className="bg-[#0F1722] p-7 hover:bg-[#1B2B3A] transition-colors"
              >
                <div className="font-display text-[#5FD4D6] text-sm tracking-[0.32em] mb-3">
                  {w.num}
                </div>
                <h3 className="font-display text-xl text-white mb-2">{w.title}</h3>
                <p className="text-sm text-[#8A95A5] leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Reviews ----------
function Reviews() {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-28 md:py-36 bg-[#080C13]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-16 max-w-2xl mx-auto reveal">
          <div className="bt-eyebrow mb-5 justify-center">Client Word</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1] text-white">
            Trusted By The People
            <br />
            <span className="text-[#5FD4D6]">Who Live In It.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 reveal">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} data-testid={`review-card-${i}`} className="bt-card p-8">
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, j) => (
                  <Star
                    key={j}
                    size={16}
                    className="fill-[#5FD4D6] text-[#5FD4D6]"
                    strokeWidth={2}
                  />
                ))}
              </div>
              <p className="text-white/85 text-base leading-relaxed mb-7">“{t.quote}”</p>
              <div className="pt-5 border-t border-[#2A3F54]">
                <div className="font-display text-white tracking-tight">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#8A95A5] mt-1">
                  {t.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Contact ----------
function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", msg: "Please fill in name, email, and project details." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Got it. We'll be in touch within one business day.",
      });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please call us at 242 436-2550.";
      setStatus({ state: "error", msg: String(msg) });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5 reveal">
          <div className="bt-eyebrow mb-5">Start The Build</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1] text-white mb-7">
            Free Estimate.
            <br />
            <span className="text-[#5FD4D6]">No Pressure.</span>
          </h2>
          <p className="text-[#B6C0CC] text-lg mb-10 max-w-md">
            Tell us about your project. We&apos;ll schedule an on-site walkthrough and deliver a
            fixed, written quote within 48 hours.
          </p>

          <div className="space-y-5">
            <a href="tel:+12424362550" data-testid="contact-phone" className="flex items-center gap-4 group">
              <div className="h-12 w-12 border border-[#2A3F54] flex items-center justify-center text-[#5FD4D6] group-hover:bg-[#5FD4D6] group-hover:text-[#0F1722] transition-colors">
                <Phone size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8A95A5]">Call</div>
                <div className="font-display text-lg text-white">242 436-2550</div>
              </div>
            </a>
            <a
              href="mailto:hello@buildingtrends.co"
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="h-12 w-12 border border-[#2A3F54] flex items-center justify-center text-[#5FD4D6] group-hover:bg-[#5FD4D6] group-hover:text-[#0F1722] transition-colors">
                <Mail size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8A95A5]">Email</div>
                <div className="font-display text-lg text-white">hello@buildingtrends.co</div>
              </div>
            </a>
            <div data-testid="contact-service-area" className="flex items-center gap-4">
              <div className="h-12 w-12 border border-[#2A3F54] flex items-center justify-center text-[#5FD4D6]">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8A95A5]">Service Area</div>
                <div className="font-display text-lg text-white">Abaco, Bahamas</div>
              </div>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-[#1B2B3A]/60 border border-[#2A3F54] p-8 md:p-12 reveal"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] text-[#8A95A5] mb-2">
                Full Name *
              </label>
              <input
                data-testid="contact-input-name"
                name="name"
                value={form.name}
                onChange={onChange}
                className="bt-input"
                placeholder="Jane Doe"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] text-[#8A95A5] mb-2">
                Email *
              </label>
              <input
                data-testid="contact-input-email"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className="bt-input"
                placeholder="jane@email.com"
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] text-[#8A95A5] mb-2">
                Phone
              </label>
              <input
                data-testid="contact-input-phone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className="bt-input"
                placeholder="242 436-2550"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] text-[#8A95A5] mb-2">
                Service
              </label>
              <select
                data-testid="contact-input-service"
                name="service"
                value={form.service}
                onChange={onChange}
                className="bt-input"
              >
                <option value="">Select</option>
                <option value="Roofing">Roofing</option>
                <option value="Construction">Construction</option>
                <option value="Renovations">Renovations</option>
                <option value="Exteriors">Exteriors</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-[0.22em] text-[#8A95A5] mb-2">
                Tell Us About The Project *
              </label>
              <textarea
                data-testid="contact-input-message"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={5}
                className="bt-input resize-none"
                placeholder="Scope, timeline, dreams..."
                required
              />
            </div>
          </div>

          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={status.state === "loading"}
            className="bt-btn-primary mt-8 w-full sm:w-auto justify-center disabled:opacity-60"
          >
            {status.state === "loading" ? "Sending..." : "Request Estimate"}
            {status.state !== "loading" && <ArrowRight size={16} />}
          </button>

          {status.state === "success" && (
            <div
              data-testid="contact-form-success"
              className="mt-6 border border-[#5FD4D6]/40 bg-[#5FD4D6]/10 text-[#5FD4D6] px-5 py-4 text-sm"
            >
              {status.msg}
            </div>
          )}
          {status.state === "error" && (
            <div
              data-testid="contact-form-error"
              className="mt-6 border border-red-500/40 bg-red-500/10 text-red-300 px-5 py-4 text-sm"
            >
              {status.msg}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

// ---------- Footer ----------
function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[#2A3F54] bg-[#080C13]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <LogoBlock />
          <p className="mt-5 text-[#8A95A5] text-sm leading-relaxed max-w-sm">
            Full-service roofing and construction studio in Abaco. Built to last. Designed to
            stand out.
          </p>
        </div>
        <div className="md:col-span-3">
          <div className="bt-eyebrow mb-5">Studio</div>
          <ul className="space-y-3 text-sm">
            {[
              ["#services", "Services"],
              ["#projects", "Projects"],
              ["#why", "Why Us"],
              ["#reviews", "Reviews"],
              ["#contact", "Contact"],
            ].map(([href, label]) => (
              <li key={href}>
                <a href={href} className="text-white/80 hover:text-[#5FD4D6] transition-colors">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <div className="bt-eyebrow mb-5">Reach Us</div>
          <ul className="space-y-3 text-sm text-white/80">
            <li>242 436-2550</li>
            <li>hello@buildingtrends.co</li>
            <li>Abaco, Bahamas</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#2A3F54]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col sm:flex-row justify-between gap-3 text-xs text-[#8A95A5]">
          <span>© {new Date().getFullYear()} Building Trends. All rights reserved.</span>
          <span className="tracking-[0.2em] uppercase">Licensed · Insured · Abaco</span>
        </div>
      </div>
    </footer>
  );
}

// ---------- App ----------
function App() {
  useRevealAll();
  return (
    <div className="App bg-[#0F1722] text-white">
      <Nav />
      <main>
        <Hero />
        <MarqueeTicker />
        <Services />
        <Projects />
        <Why />
        <Reviews />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
